import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { Plus } from "lucide-react";

import Breadcrumbs from "../../../components/BreadCrumbs";
import Filters from "../../../components/Filter";
import AddReportModal from "../../../components/Events/Event/Reports/AddReportModal";
import ReportsEmptyState from "../../../components/Events/Event/Reports/ReportsEmptyState";
import ReportsTable from "../../../components/Events/Event/Reports/ReportsTable";
import { UIButton, UIPaginationBar } from "../../../components/ui";

import "./Reports.scss";

const ReportsListView = ({
  breadcrumbLabel,
  title,
  canAdd,
  addReportLabel,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  eventFilter,
  eventOptions,
  onEventFilterChange,
  statusFilter,
  statusOptions,
  onStatusFilterChange,
  rows,
  page,
  pageSize,
  paginationPages,
  paginationFrom,
  paginationTo,
  totalCount,
  showingRangeTemplate,
  rowsPerPageLabel,
  onPageChange,
  onPageSizeChange,
  emptyTitle,
  emptyDescription,
  emptyActionLabel,
  hasActiveFilters,
  isLoading,
  showAddModal,
  onOpenAddModal,
  onCloseAddModal,
  onCreateReport,
  creating,
  addModalLabels,
  addEventOptions,
  addZoneOptions,
  onAddEventChange,
  tableLabels,
  onRowClick,
}) => {
  const renderContent = () => {
    if (isLoading) return null;

    if (!rows.length) {
      return (
        <ReportsEmptyState
          title={emptyTitle}
          description={emptyDescription}
          actionLabel={emptyActionLabel || addReportLabel}
          onAction={onOpenAddModal}
          showAction={!hasActiveFilters && canAdd}
        />
      );
    }

    return (
      <>
        <ReportsTable
          rows={rows}
          labels={tableLabels}
          pageSize={pageSize}
          onRowClick={onRowClick}
        />
        {totalCount > 0 ? (
          <UIPaginationBar
            currentPage={page}
            pages={paginationPages}
            pageSize={pageSize}
            rangeTemplate={showingRangeTemplate}
            rangeFrom={paginationFrom}
            rangeTo={paginationTo}
            rangeTotal={totalCount}
            rowsPerPageLabel={rowsPerPageLabel}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            className="reports-list-pagination"
          />
        ) : null}
      </>
    );
  };

  return (
    <div className="company-reports">
      <div className="page-title-margin">
        <Breadcrumbs
          list={[{ path: "", label: breadcrumbLabel }]}
          BreadCrumbHasBackIcon={false}
        />
        <Box className="company-reports__header">
          <Typography component="h1" className="ui-text-heading-lg">
            {title}
          </Typography>
        </Box>
      </div>

      <Filters
        isShowSearch
        isCollapsable
        renderActionBeforeFilterToggle={
          canAdd ? (
            <UIButton
              type="button"
              variant="primary"
              appearance="outline"
              size="large"
              startIcon={<Plus size={16} aria-hidden />}
              onClick={onOpenAddModal}
            >
              {addReportLabel}
            </UIButton>
          ) : null
        }
        searchInput={{
          placeholder: searchPlaceholder,
          name: "search",
          value: searchValue,
          onChange: onSearchChange,
        }}
        filters={[
          {
            type: "filter",
            name: "event",
            placeholder: addModalLabels.eventPlaceholder,
            value: eventFilter,
            options: eventOptions,
            onChange: onEventFilterChange,
          },
          {
            type: "filter",
            name: "status",
            placeholder: tableLabels.status,
            value: statusFilter,
            options: statusOptions,
            onChange: onStatusFilterChange,
          },
        ]}
        classNames={{ container: "reports-list-filters" }}
      />

      {renderContent()}

      <AddReportModal
        open={showAddModal}
        labels={addModalLabels}
        showEventSelect
        eventOptions={addEventOptions}
        zoneOptions={addZoneOptions}
        submitting={creating}
        onClose={onCloseAddModal}
        onSubmit={onCreateReport}
        onEventChange={onAddEventChange}
      />
    </div>
  );
};

ReportsListView.propTypes = {
  breadcrumbLabel: PropTypes.node.isRequired,
  title: PropTypes.node.isRequired,
  canAdd: PropTypes.bool,
  addReportLabel: PropTypes.node,
  searchPlaceholder: PropTypes.string,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  eventFilter: PropTypes.object,
  eventOptions: PropTypes.array,
  onEventFilterChange: PropTypes.func.isRequired,
  statusFilter: PropTypes.object,
  statusOptions: PropTypes.array,
  onStatusFilterChange: PropTypes.func.isRequired,
  rows: PropTypes.array.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  paginationPages: PropTypes.number.isRequired,
  paginationFrom: PropTypes.number.isRequired,
  paginationTo: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  showingRangeTemplate: PropTypes.string.isRequired,
  rowsPerPageLabel: PropTypes.node,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  emptyTitle: PropTypes.node,
  emptyDescription: PropTypes.node,
  emptyActionLabel: PropTypes.node,
  hasActiveFilters: PropTypes.bool,
  isLoading: PropTypes.bool,
  showAddModal: PropTypes.bool.isRequired,
  onOpenAddModal: PropTypes.func.isRequired,
  onCloseAddModal: PropTypes.func.isRequired,
  onCreateReport: PropTypes.func.isRequired,
  creating: PropTypes.bool,
  addModalLabels: PropTypes.object.isRequired,
  addEventOptions: PropTypes.array,
  addZoneOptions: PropTypes.array,
  onAddEventChange: PropTypes.func.isRequired,
  tableLabels: PropTypes.object.isRequired,
  onRowClick: PropTypes.func.isRequired,
};

ReportsListView.defaultProps = {
  canAdd: false,
  addReportLabel: null,
  searchPlaceholder: "",
  searchValue: "",
  eventFilter: null,
  eventOptions: [],
  statusFilter: null,
  statusOptions: [],
  rowsPerPageLabel: null,
  emptyTitle: null,
  emptyDescription: null,
  emptyActionLabel: null,
  hasActiveFilters: false,
  isLoading: false,
  creating: false,
  addEventOptions: [],
  addZoneOptions: [],
};

export default ReportsListView;
